import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import PropTypes from 'prop-types'
import { QRCodeSVG } from 'qrcode.react'
import { Card } from 'react-bootstrap'
import { Trans, useTranslation } from 'react-i18next'
import OTPInput from 'components/Shared/OTPInput'
import { SecondaryButton } from 'components/Shared/Button'
import Modal from 'components/Shared/Modal'
import { fetchTOTPQRCode } from 'api/auth'
import { initStateObject, objectReducer } from 'reducers/default'
import LoadingSpinner, { SpinnerSize } from 'components/Shared/LoadingSpinner'
import ErrorMessage from 'components/Shared/ErrorMessage'
import { OTP_LENGTH, useCognito, useCognitoDispatch } from '../../CognitoContextProvider'

function ManualCode({ code }) {
  const { t } = useTranslation()
  return (
    <div className="setup-authenticator_manual-code">
      <p className="mb-1">{t('SetupMFA.authenticatorApp.steps.manualCode.title')}</p>
      <p>
        {t('SetupMFA.authenticatorApp.steps.manualCode.description')}
        <br />
        {code}
      </p>
    </div>
  )
}

ManualCode.propTypes = {
  code: PropTypes.string,
}

function SuccessContent({ onClose }) {
  const { t } = useTranslation()

  return (
    <div className="setup-authenticator setup-authenticator_success">
      <div className="card p-3 mt-3">
        <h2 className="card-title-default">{t('SetupMFA.authenticatorApp.success.moreInfo')}</h2>
        <div className="setup-authenticator_success__content">
          <p>{t('SetupMFA.authenticatorApp.success.moreInfoContent1')}</p>
          <br />
          <p>{t('SetupMFA.authenticatorApp.success.moreInfoContent2')}</p>
        </div>
      </div>
      <div className="setup-authenticator_btns">
        <SecondaryButton
          type="submit"
          onClick={onClose}
          text={t('common.done')}
          fullWidth={false}
        />
      </div>
    </div>
  )
}

SuccessContent.propTypes = {
  onClose: PropTypes.func,
}

function SetupAuthenticatorContent({ onSuccess }) {
  const { t } = useTranslation()
  const { setupMFATOTP } = useCognitoDispatch()
  const { loading, error, mfaSetup } = useCognito()

  const [otp, setOtp] = useState('')
  const [otpState, otpDispatch] = useReducer(objectReducer, initStateObject)

  useEffect(() => {
    fetchTOTPQRCode(otpDispatch, mfaSetup.accessToken)
  }, [mfaSetup.accessToken])

  const handleOtpChange = (otp) => {
    setOtp(otp)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setupMFATOTP(otp, ({ success }) => {
      if (success) {
        onSuccess()
      }
    })
  }

  const authenticatorLink = useMemo(() => getAuthenticatorLink(), [])

  return (
    <div className="setup-authenticator">
      <p className="mt-3">{t('SetupMFA.authenticatorApp.enrollDescription')}</p>
      <article className="how-it-works mt-3">
        <Card className="card card-default">
          <div className="card-header">
            <h2 className="card-title-default">{t('common.howItWorks')}</h2>
          </div>
          <div className="card-body">
            <ol type="1">
              <li>
                <Trans
                  i18nKey="SetupMFA.authenticatorApp.steps.1"
                  components={{
                    a: (
                      <a
                        href={authenticatorLink}
                        rel="noopener noreferrer"
                        target="_blank"
                        className="setup-authenticator_link"
                      >
                        {t('SetupMFA.authenticatorApp.steps.steps.needAnApp')}
                      </a>
                    ),
                  }}
                />
              </li>

              <li>
                <p>{t('SetupMFA.authenticatorApp.steps.2')}</p>
                <div className="setup-authenticator_subitem">
                  {otpState.loading && (
                    <LoadingSpinner className="my-3" size={SpinnerSize.LARGE} showText={false} />
                  )}
                  {otpState.error && <ErrorMessage danger>{t(otpState.message)}</ErrorMessage>}
                  {otpState.content.qr_uri && <QRCodeSVG value={otpState.content.qr_uri} />}
                  {otpState.content.qr_secret && (
                    <ManualCode code={otpState.content.qr_secret} />
                  )}
                </div>
              </li>
              <li>
                {t('SetupMFA.authenticatorApp.steps.3')}
                <div className="setup-authenticator_subitem">
                  <OTPInput
                    length={OTP_LENGTH}
                    onChange={handleOtpChange}
                    variant={OTPInput.OTPVariant.small}
                    disabled={loading || !otpState.content.qr_uri}
                  />
                </div>
              </li>
            </ol>
          </div>
        </Card>
        <div className="setup-authenticator_btns">
          {error && <p className="setup-authenticator_btns_error warning-red">{t(error)}</p>}
          <SecondaryButton
            type="submit"
            onClick={handleSubmit}
            text={t('SetupMFA.authenticatorApp.verify')}
            fullWidth={false}
            disabled={loading || otp.length !== OTP_LENGTH}
          />
        </div>
      </article>
    </div>
  )
}

SetupAuthenticatorContent.propTypes = {
  onSuccess: PropTypes.func,
}

export default function SetupAuthenticator({ isOpen, onClose, onSuccess }) {
  const { t } = useTranslation()
  const [success, setSuccess] = useState(false)
  const { setError } = useCognitoDispatch()

  const onCloseInternal = useCallback(() => {
    setError(null)
    onClose()
  }, [onClose, setError])

  if (!isOpen) return null

  if (success) {
    return (
      <Modal
        headerRenderer={() => (
          <div className="setup-authenticator_success__header">
            <h2 className="modal_inner_header_title">{`${t('common.success')}!!`}</h2>
            <p className="setup-authenticator_success__header__description">{t('SetupMFA.authenticatorApp.success.description')}</p>
          </div>
        )}
        fullWidth
        offWhiteBg
        contentClasses="w-md-60"
        hideCloseBtn
        mainContent={<SuccessContent onClose={onSuccess} />}
      />
    )
  }

  return (
    <Modal
      title={t('SetupMFA.authenticatorApp.enroll')}
      fullWidth
      offWhiteBg
      contentClasses="w-md-60"
      closeCallback={onCloseInternal}
      onClickOutside={onCloseInternal}
      mainContent={
        <SetupAuthenticatorContent
          onSuccess={() => {
            setSuccess(true)
          }}
        />
      }
    />
  )
}

SetupAuthenticator.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
}

const getAuthenticatorLink = () => {
  const userAgent = navigator.userAgent || navigator.vendor

  if (/android/i.test(userAgent)) {
    return 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2'
  } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'https://apps.apple.com/app/google-authenticator/id388497605'
  }
  return 'https://www.google.com/search?q=Google+Authenticator+app'
}
